import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Add,
  Send,
  LocalAtm,
  Whatshot,
} from '@mui/icons-material';
import { getApiUrl } from '../config';
import TransactionLedger from './TransactionLedger';
import TransferDialog from './TransferDialog';
import {
  GlassButton,
  GlassInfoCard,
  GlassCard,
  GlassDialog,
  GlassTextField,
} from './ui/GlassComponents';

const TreasuryManagement = ({ nationName, sessionData }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [treasury, setTreasury] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [treasuries, setTreasuries] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialog states
  const [mintDialog, setMintDialog] = useState(false);
  const [burnDialog, setBurnDialog] = useState(false);
  const [transferDialog, setTransferDialog] = useState(false);
  const [transactionLedgerDialog, setTransactionLedgerDialog] = useState(false);
  const [createCurrencyDialog, setCreateCurrencyDialog] = useState(false);

  // Form states
  const [mintForm, setMintForm] = useState({
    amount: '',
    description: '',
  });
  const [burnForm, setBurnForm] = useState({
    currencyId: '',
    amount: '',
    description: '',
  });
  const [newCurrency, setNewCurrency] = useState({
    name: '',
    issuingNation: nationName,
  });

  // Fetch functions
  const fetchTreasuryDetails = useCallback(async () => {
    if (!nationName) return;
    
    setLoading(true);
    try {
      const response = await fetch(getApiUrl(`/treasuries/${nationName}`), {
        headers: {
          'Authorization': `Bearer ${sessionData.token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch treasury details');
      }
      
      const data = await response.json();
      setTreasury(data);
    } catch (err) {
      setError('Failed to load treasury details: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [nationName, sessionData.token]);

  const fetchCurrencies = useCallback(async () => {
    try {
      const response = await fetch(getApiUrl('/currencies'), {
        headers: {
          'Authorization': `Bearer ${sessionData.token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch currencies');
      }
      
      const data = await response.json();
      setCurrencies(data.currencies || []);
    } catch (err) {
      setError('Failed to load currencies: ' + err.message);
    }
  }, [sessionData.token]);

  const fetchTreasuries = useCallback(async () => {
    try {
      const response = await fetch(getApiUrl('/treasuries'), {
        headers: {
          'Authorization': `Bearer ${sessionData.token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch treasuries');
      }
      
      const data = await response.json();
      setTreasuries(data.treasuries || []);
    } catch (err) {
      setError('Failed to load treasuries: ' + err.message);
    }
  }, [sessionData.token]);

  const fetchBanks = useCallback(async () => {
    try {
      const response = await fetch(getApiUrl('/banks'), {
        headers: {
          'Authorization': `Bearer ${sessionData.token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch banks');
      }
      
      const data = await response.json();
      setBanks(data.banks || []);
    } catch (err) {
      setError('Failed to load banks: ' + err.message);
    }
  }, [sessionData.token]);

  useEffect(() => {
    if (nationName) {
      fetchTreasuryDetails();
      fetchCurrencies();
      fetchTreasuries();
      fetchBanks();
    }
  }, [nationName, fetchTreasuryDetails, fetchCurrencies, fetchTreasuries, fetchBanks]);

  // Get the currency issued by this nation
  const getNationCurrency = () => {
    return currencies.find(currency => currency.issuingNation === nationName);
  };

  const handleCreateCurrency = async () => {
    if (!newCurrency.name) {
      setError('Please fill in the currency name');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(getApiUrl('/currencies'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.token}`,
        },
        body: JSON.stringify({
          ...newCurrency,
          createdBy: sessionData.selectedDisplayName,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create currency');
      }

      setCreateCurrencyDialog(false);
      setNewCurrency({ name: '', issuingNation: nationName });
      await fetchCurrencies();
      setError('');
      setSuccess('Currency created successfully! Opening mint dialog...');
      setTimeout(() => setSuccess(''), 3000);
      // Automatically open mint dialog after creating currency
      setTimeout(() => setMintDialog(true), 500);
    } catch (err) {
      setSuccess('');
      setError('Failed to create currency: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMintCurrency = async () => {
    if (!mintForm.amount || !treasury) {
      setError('Please fill in all required fields');
      return;
    }

    const currency = getNationCurrency();
    if (!currency) {
      setError('Currency not found. Please create a currency first.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(getApiUrl(`/treasuries/${treasury.nationName}/mint`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.token}`,
        },
        body: JSON.stringify({
          currencySymbol: currency.symbol,
          amount: parseInt(mintForm.amount),
          description: mintForm.description,
          mintedBy: sessionData.selectedDisplayName,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to mint currency');
      }

      setMintDialog(false);
      setMintForm({ amount: '', description: '' });
      await fetchTreasuryDetails();
      await fetchCurrencies();
      setError('');
      setSuccess('Currency minted successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setSuccess('');
      setError('Failed to mint currency: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBurnCurrency = async () => {
    if (!burnForm.currencyId || !burnForm.amount || !treasury) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(getApiUrl(`/treasuries/${treasury.nationName}/burn`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.token}`,
        },
        body: JSON.stringify({
          currencySymbol: burnForm.currencyId,
          amount: parseInt(burnForm.amount),
          description: burnForm.description,
          burnedBy: sessionData.selectedDisplayName,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to burn currency');
      }

      setBurnDialog(false);
      setBurnForm({ currencyId: '', amount: '', description: '' });
      await fetchTreasuryDetails();
      await fetchCurrencies();
      setError('');
      setSuccess('Currency burned successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setSuccess('');
      setError('Failed to burn currency: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTransferSuccess = (result) => {
    setSuccess(`Transfer completed successfully: ${result.transfer.amount} ${result.transfer.currencySymbol}`);
    setError('');
    setTransferDialog(false);
    // Refresh treasury data
    fetchTreasuryDetails();
  };

  const formatCurrency = (amount, symbol) => {
    return `${symbol}${amount.toLocaleString()}`;
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString();
  };

  if (!nationName) {
    return (
      <GlassInfoCard severity="info">
        Select a nation to view treasury management.
      </GlassInfoCard>
    );
  }

  if (loading && !treasury) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (!treasury) {
    return (
      <GlassInfoCard severity="warning">
        Failed to load treasury information for {nationName}.
      </GlassInfoCard>
    );
  }

  return (
    <Box>
      {error && (
        <GlassInfoCard severity="error" onClose={() => setError('')} sx={{ mb: 3 }}>
          {error}
        </GlassInfoCard>
      )}

      {success && (
        <GlassInfoCard severity="success" onClose={() => setSuccess('')} sx={{ mb: 3 }}>
          {success}
        </GlassInfoCard>
      )}

      {/* Treasury Balances */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 600 }}>Treasury Balances</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <GlassButton
              variant="glass"
              size="small"
              onClick={() => {
                const currency = getNationCurrency();
                if (!currency) {
                  // Nation doesn't have a currency, show create currency dialog
                  setCreateCurrencyDialog(true);
                } else {
                  // Nation has a currency, show mint dialog
                  setMintDialog(true);
                }
              }}
            >
              <Add sx={{ fontSize: '1rem', mr: 0.5 }} />
              {!isMobile && 'Mint Currency'}
            </GlassButton>
            <GlassButton
              variant="glass"
              size="small"
              onClick={() => setBurnDialog(true)}
            >
              <Whatshot sx={{ fontSize: '1rem', mr: isMobile ? 0 : 0.5 }} />
              {!isMobile && 'Burn Currency'}
            </GlassButton>
            <GlassButton
              variant="glass"
              size="small"
              onClick={() => setTransferDialog(true)}
            >
              <Send sx={{ fontSize: '1rem', mr: 0.5 }} />
              {!isMobile && 'Transfer'}
            </GlassButton>
            <GlassButton
              variant="glass"
              size="small"
              onClick={() => setTransactionLedgerDialog(true)}
            >
              <LocalAtm sx={{ fontSize: '1rem', mr: 0.5 }} />
              {!isMobile && 'History'}
            </GlassButton>
          </Box>
        </Box>
        
        {treasury.balances.length === 0 ? (
          <GlassInfoCard severity="info">
            No currency balances. Use the "Mint Currency" button to mint your nation's currency.
          </GlassInfoCard>
        ) : (
          <TableContainer component={GlassCard} sx={{ overflow: 'visible' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>Currency</TableCell>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>Symbol</TableCell>
                  <TableCell align="right" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {treasury.balances.map((balance) => (
                  <TableRow key={balance.currencyId}>
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.85)' }}>{balance.currencyName}</TableCell>
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.85)' }}>{balance.currencySymbol}</TableCell>
                    <TableCell align="right" sx={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                      {formatCurrency(balance.balance, balance.currencySymbol)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* UBI Policies */}
      <Box>
        <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 600 }} gutterBottom>UBI Policies</Typography>
        {treasury.ubiPolicies.length === 0 ? (
          <GlassInfoCard severity="info">No UBI policies configured</GlassInfoCard>
        ) : (
          <TableContainer component={GlassCard} sx={{ overflow: 'visible' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>Currency</TableCell>
                  <TableCell align="right" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>Amount per Citizen</TableCell>
                  <TableCell align="center" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>Last Distribution</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {treasury.ubiPolicies.map((policy) => (
                  <TableRow key={policy.currencyId}>
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.85)' }}>{policy.currencyName}</TableCell>
                    <TableCell align="right" sx={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                      {formatCurrency(policy.amountPerCitizen, policy.currencySymbol)}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={policy.enabled ? 'Enabled' : 'Disabled'}
                        color={policy.enabled ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                      {policy.lastDistribution ? formatDate(policy.lastDistribution) : 'Never'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Mint Currency Dialog */}
      <GlassDialog
        open={mintDialog}
        onClose={() => setMintDialog(false)}
        title="Mint Currency"
        actions={
          <>
            <GlassButton variant="glass" onClick={() => setMintDialog(false)}>
              Cancel
            </GlassButton>
            <GlassButton variant="glass" onClick={handleMintCurrency} disabled={loading}>
              {loading ? <CircularProgress size={20} /> : 'Mint'}
            </GlassButton>
          </>
        }
      >
        {treasury && (
          <>
            {(() => {
              const currency = getNationCurrency();
              return currency ? (
                <GlassInfoCard severity="info">
                  Minting <strong>{currency.name} ({currency.symbol})</strong> for {treasury.nationName}
                </GlassInfoCard>
              ) : null;
            })()}
          </>
        )}
        <GlassTextField
          fullWidth
          label="Amount"
          type="number"
          value={mintForm.amount}
          onChange={(e) => setMintForm({ ...mintForm, amount: e.target.value })}
          margin="normal"
          required
        />
        <GlassTextField
          fullWidth
          label="Description (optional)"
          value={mintForm.description}
          onChange={(e) => setMintForm({ ...mintForm, description: e.target.value })}
          margin="normal"
          multiline
          rows={2}
        />
      </GlassDialog>

      {/* Burn Currency Dialog */}
      <GlassDialog
        open={burnDialog}
        onClose={() => setBurnDialog(false)}
        title="Burn Currency"
        actions={
          <>
            <GlassButton variant="glass" onClick={() => setBurnDialog(false)}>
              Cancel
            </GlassButton>
            <GlassButton variant="glass" onClick={handleBurnCurrency} disabled={loading}>
              {loading ? <CircularProgress size={20} /> : 'Burn'}
            </GlassButton>
          </>
        }
      >
        {treasury && (
          <>
            <GlassInfoCard severity="warning">
              Burning currency will permanently remove it from circulation.
            </GlassInfoCard>
            <FormControl fullWidth margin="normal" required
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                  '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&.Mui-focused fieldset': { borderColor: 'rgba(255, 255, 255, 0.4)' },
                },
                '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' },
                '& .MuiInputLabel-root.Mui-focused': { color: 'rgba(255, 255, 255, 0.9)' },
                '& .MuiSelect-icon': { color: 'rgba(255, 255, 255, 0.6)' },
              }}
            >
              <InputLabel>Currency</InputLabel>
              <Select
                value={burnForm.currencyId}
                onChange={(e) => setBurnForm({ ...burnForm, currencyId: e.target.value })}
                label="Currency"
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: 'rgba(30, 30, 40, 0.95)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      '& .MuiMenuItem-root': {
                        color: 'white',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.1)',
                        },
                        '&.Mui-selected': {
                          bgcolor: 'rgba(255, 255, 255, 0.15)',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.2)',
                          },
                        },
                      },
                    },
                  },
                }}
              >
                {treasury.balances.map((balance) => (
                  <MenuItem key={balance.currencySymbol} value={balance.currencySymbol}>
                    {balance.currencyName} ({balance.currencySymbol}) - Balance: {formatCurrency(balance.balance, balance.currencySymbol)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {treasury.balances.length === 0 && (
              <GlassInfoCard severity="warning" sx={{ mt: 2 }}>
                No currency balances available to burn. Mint some currency first.
              </GlassInfoCard>
            )}
          </>
        )}
        <GlassTextField
          fullWidth
          label="Amount to Burn"
          type="number"
          value={burnForm.amount}
          onChange={(e) => setBurnForm({ ...burnForm, amount: e.target.value })}
          margin="normal"
          required
        />
        <GlassTextField
          fullWidth
          label="Description (optional)"
          value={burnForm.description}
          onChange={(e) => setBurnForm({ ...burnForm, description: e.target.value })}
          margin="normal"
          multiline
          rows={2}
        />
      </GlassDialog>

      {/* Transfer Dialog */}
      <TransferDialog
        open={transferDialog}
        onClose={() => setTransferDialog(false)}
        onTransfer={handleTransferSuccess}
        availableCurrencies={treasury?.balances?.map((balance) => ({
          symbol: balance.currencySymbol,
          name: balance.currencyName
        })) || []}
        userDisplayName={sessionData?.selectedDisplayName}
        sessionData={sessionData}
        prefillFromType="treasury"
        prefillFromId={nationName}
        prefillFromName={`${nationName} Treasury`}
      />

      {/* Create Currency Dialog */}
      <GlassDialog
        open={createCurrencyDialog}
        onClose={() => setCreateCurrencyDialog(false)}
        title={`Create Currency for ${nationName}`}
        actions={
          <>
            <GlassButton variant="glass" onClick={() => setCreateCurrencyDialog(false)}>
              Cancel
            </GlassButton>
            <GlassButton variant="glass" onClick={handleCreateCurrency} disabled={loading}>
              {loading ? <CircularProgress size={20} /> : 'Create Currency'}
            </GlassButton>
          </>
        }
      >
        <GlassInfoCard severity="info">
          Creating a currency for {nationName}. The currency symbol will be automatically generated from your nation's abbreviation.
        </GlassInfoCard>
        <GlassTextField
          fullWidth
          label="Currency Name"
          value={newCurrency.name}
          onChange={(e) => setNewCurrency({ ...newCurrency, name: e.target.value })}
          margin="normal"
          required
          placeholder="e.g., US Dollar, Euro, Yen"
        />
        <GlassTextField
          fullWidth
          label="Issuing Nation"
          value={newCurrency.issuingNation}
          margin="normal"
          disabled
          helperText="This is automatically set to the current nation"
        />
      </GlassDialog>

      {/* Transaction Ledger Dialog */}
      <TransactionLedger
        open={transactionLedgerDialog}
        onClose={() => setTransactionLedgerDialog(false)}
        entityType="treasury"
        entityId={nationName}
        entityName={`${nationName} Treasury`}
        sessionData={sessionData}
      />
    </Box>
  );
};

export default TreasuryManagement; 